/**
 * Atmospheric stability derived from a sounding (SPEC §3.3).
 *
 * The Brunt–Väisälä frequency N² = (g/θ)(dθ/dz) is the number that actually
 * matters: it sets the Froude regime, the lee wavelength, and — through the
 * stability dial — whether Layer 1 sends air over terrain or around it.
 */

const G = 9.80665;
/** dry adiabatic lapse rate, K/m */
export const DALR = 0.0098;
const RD = 287.05;
const CP = 1005;
const P0 = 1000; // hPa

export interface SoundingLevel {
  /** pressure, hPa */
  pressure: number;
  /** temperature, °C */
  temperature: number;
  /** geopotential height, metres */
  height: number;
}

export interface StabilityState {
  /** Brunt–Väisälä frequency, rad/s. Zero or below means unstable. */
  brunt: number;
  /** N², s⁻² */
  n2: number;
  /** environmental lapse rate, K/m */
  lapseRate: number;
  /** the dial value, −1 unstable … +1 stable */
  dial: number;
  /** why the dial ended up where it did, for the UI read-out */
  basis: string;
}

/** Potential temperature, K. */
export function potentialTemperature(tempC: number, pressureHPa: number): number {
  return (tempC + 273.15) * Math.pow(P0 / pressureHPa, RD / CP);
}

/**
 * N² from the two sounding levels that bracket the layer the pilot flies in.
 * Levels are sorted by height and the lowest pair spanning at least 300 m is
 * used — thinner layers are dominated by measurement noise in the model output.
 */
export function bruntVaisala(levels: SoundingLevel[]): { n2: number; lapseRate: number } | null {
  const sorted = levels
    .filter((l) => Number.isFinite(l.height) && Number.isFinite(l.temperature))
    .sort((a, b) => a.height - b.height);
  if (sorted.length < 2) return null;

  const lower = sorted[0];
  let upper = sorted[sorted.length - 1];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].height - lower.height >= 300) {
      upper = sorted[i];
      break;
    }
  }

  const dz = upper.height - lower.height;
  if (dz < 50) return null;

  const thLow = potentialTemperature(lower.temperature, lower.pressure);
  const thHigh = potentialTemperature(upper.temperature, upper.pressure);
  const thMean = (thLow + thHigh) / 2;

  const n2 = (G / thMean) * ((thHigh - thLow) / dz);
  const lapseRate = -(upper.temperature - lower.temperature) / dz;
  return { n2, lapseRate };
}

/**
 * Map the sounding onto the single stability dial (SPEC §4).
 *
 * The mapping is deliberately blunt: N² is the primary signal, with CAPE and a
 * deep boundary layer pulling towards the unstable end. It is a dial the pilot
 * can override, not a measurement.
 */
export function deriveStability(
  levels: SoundingLevel[],
  opts: { cape?: number; blHeight?: number } = {}
): StabilityState {
  const bv = bruntVaisala(levels);
  const cape = opts.cape ?? 0;
  const bl = opts.blHeight ?? 1200;

  if (!bv) {
    return {
      brunt: 0.01,
      n2: 1e-4,
      lapseRate: DALR,
      dial: 0,
      basis: 'no sounding available — assuming neutral',
    };
  }

  const { n2, lapseRate } = bv;
  const brunt = n2 > 0 ? Math.sqrt(n2) : 0;

  // N² ≈ 1e−4 is a well-mixed convective boundary layer; 4e−4 is a solidly
  // stable morning inversion. Map that span onto the dial.
  let dial = Math.max(-1, Math.min(1, (n2 - 1.0e-4) / 2.4e-4));

  // A deep boundary layer and real CAPE both mean convection is doing the
  // mixing regardless of what the mean lapse rate says.
  if (cape > 200) dial -= 0.3;
  if (cape > 800) dial -= 0.2;
  if (bl > 1800) dial -= 0.2;
  if (bl < 600) dial += 0.2;
  dial = Math.max(-1, Math.min(1, dial));

  const descriptor =
    dial > 0.45 ? 'stable' : dial < -0.45 ? 'unstable' : 'near-neutral';
  const basis =
    `N² = ${n2.toExponential(1)} s⁻², lapse ${(lapseRate * 1000).toFixed(1)} K/km` +
    `${cape > 200 ? `, CAPE ${Math.round(cape)} J/kg` : ''}` +
    `, BL ${Math.round(bl)} m → ${descriptor}`;

  return { brunt: Math.max(brunt, 1e-4), n2, lapseRate, dial, basis };
}

/** Fallback N for the manual mode, where there is no sounding to read. */
export function bruntFromDial(dial: number): number {
  // Spans ~0.005 rad/s (well mixed) to ~0.022 rad/s (strong inversion).
  const n2 = 1.0e-4 + dial * 2.0e-4;
  return Math.sqrt(Math.max(2.5e-5, n2));
}
